import React, { useState } from 'react';
import {
  Button,
  Confirmation,
  Menu,
  Popover,
  TextArea,
  useToasts,
} from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import {
  useBulkActionsResponse,
  useGetWorkflows,
} from 'routes/screens/response/hooks';
import { BulkActionsContainer } from 'routes/screens/response/style';
import {
  BulkActionsResponse,
  ResponseBulkActionType,
} from 'routes/screens/response/types';
import Icon from 'shared/components/Icon';
import { LastSeparateMenu } from 'shared/styles';
import { ConfirmationType } from 'shared/types';
import ResponseTags from '../../../view/components/ResponseTags';
import Workflow from '../../../view/components/Workflow';
import { StyledInvalidMessage } from '../../../view/style';
import i18n from './../../../../../../../../src/i18n';
import SearchTagsAction from './add-tags';
import TriggerWorkflowAction from './trigger-workflow';

type ResponseBulkActionsPropsType = {
  selectedRows: string[];
  setSelectedRowIds: (rowIds: Record<string, boolean>) => void;
  isLoading: boolean;
  surveyId: string;
  setConfirmationData?: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  setIsResponsesDeleted: (value: Record<string, boolean>) => void;
};

function ResponsesBulkActions(props: ResponseBulkActionsPropsType) {
  const { t } = useTranslation();
  const {
    selectedRows,
    isLoading,
    surveyId,
    setConfirmationData,
    setSelectedRowIds,
    setIsResponsesDeleted,
  } = props;
  const [isAddTagsDrawerOpen, setIsAddTagsDrawerOpen] =
    React.useState<boolean>(false);
  const [isTriggerWorkflowDrawerOpen, setIsTriggerWorkflowDrawerOpen] =
    React.useState(false);
  const [isMarkInvalidDrawerOpen, setisMarkInvalidDrawerOpen] =
    useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const [workflowList, setWorkflowList] = React.useState<any>([]);
  const [, setToast] = useToasts();
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const [invalidMessage, setInvalidMessage] = useState({
    isTouched: false,
    message: '',
  });

  React.useEffect(() => {
    if (allWorkflows.length) {
      setWorkflowList(
        allWorkflows.filter(
          (item: any) => item.type === 'MANUAL' && !item.deleted && item.active,
        ),
      );
    }
  }, []);

  const { data: allWorkflows = [], isLoading: isWorkflowsLoading } =
    useGetWorkflows();
  const handleWorkflowChange = (value) => {
    setWorkflowList(
      allWorkflows.filter(
        (item: any) =>
          item.type === 'MANUAL' &&
          !item.deleted &&
          item.active &&
          (item?.name || '')
            .toLowerCase()
            .includes(value?.trim().toLowerCase()),
      ),
    );
  };

  const { mutate: doBulkAction, isLoading: isBulkActionLoading } =
    useBulkActionsResponse({
      setToast,
      callback: (isSuccess: boolean, response: boolean, { payload }: any) => {
        const actionType = payload.response_bulk_action_type.toLowerCase();
        if (!isSuccess) {
          setSelectedItems([]);
          setToast({
            delay: 3000,
            type: 'error',
            text: `${i18n.t(`forms.toast.error.bulk_${actionType}_error`)}`,
          });
        } else {
          if (setConfirmationData) setConfirmationData({});
          setToast({
            delay: 3000,
            type: 'success',
            text: `${i18n.t(`forms.toast.success.bulk_${actionType}_success`)}`,
          });
        }
        setTimeout(() => setSelectedRowIds({}), 2000);
      },
    });

  const options = [
    {
      id: ResponseBulkActionType.MARK_INVALID,
      name: `${t('workflow.action.action_type.mark_as_invalid')}`,
      disabled: false,
    },
    {
      id: ResponseBulkActionType.MARK_AS_NONACTIONABLE,
      name: `${t('workflow.action.action_type.non_actionable')}`,
      disabled: false,
    },
    {
      id: ResponseBulkActionType.DELETE_RESPONSE,
      name: `${t('responses.bulk_actions.delete')}`,
      disabled: false,
    },
  ];

  // here values might be tag ids or workflow ids since we are re-using the components
  const handleBulkAction = (
    actionType: ResponseBulkActionType,
    values: string[] = [],
  ) => {
    let payload: BulkActionsResponse;

    switch (actionType) {
      case ResponseBulkActionType.ADD_TAGS:
        payload = {
          response_bulk_action_type: ResponseBulkActionType.ADD_TAGS,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: { tag_ids: values },
        };
        doBulkAction({ payload });
        setIsAddTagsDrawerOpen(false);
        break;
      case ResponseBulkActionType.TRIGGER_WORKFLOW:
        payload = {
          response_bulk_action_type: ResponseBulkActionType.TRIGGER_WORKFLOW,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: {
            workflow_ids: values,
          },
        };
        doBulkAction({ payload });
        setIsTriggerWorkflowDrawerOpen(false);
        break;
      case ResponseBulkActionType.MARK_AS_FAVOURITE:
        payload = {
          response_bulk_action_type: ResponseBulkActionType.MARK_AS_FAVOURITE,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: {},
        };
        doBulkAction({ payload });
        break;
      case ResponseBulkActionType.MARK_AS_NONACTIONABLE:
        payload = {
          response_bulk_action_type:
            ResponseBulkActionType.MARK_AS_NONACTIONABLE,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: {},
        };
        doBulkAction({ payload });
        break;
      case ResponseBulkActionType.MARK_INVALID:
        payload = {
          response_bulk_action_type: ResponseBulkActionType.MARK_INVALID,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: { invalid_reason: invalidMessage?.message.trim() },
        };
        doBulkAction({ payload });
        setisMarkInvalidDrawerOpen(false);
        setInvalidMessage({ isTouched: false, message: '' });
        break;
      case ResponseBulkActionType.DELETE_RESPONSE:
        payload = {
          response_bulk_action_type: ResponseBulkActionType.DELETE_RESPONSE,
          survey_id: surveyId,
          response_ids: selectedRows,
          values: {},
        };
        if (setConfirmationData) {
          setConfirmationData({
            id: new Date().toISOString(),
            title: `${t('delete') + ' ' + t('sidebar.responses')}`,
            className: 'error',
            fill: '#d7461f',
            confirmButtonText: `${t('delete')}`,
            onConfirm: () => {
              setConfirmationData((old) => ({
                ...old,
                confirmButtonDisabled: true,
              }));
              doBulkAction({ payload });
              setIsResponsesDeleted({ [surveyId]: true });
            },
            infoText: `${t('contacts.menu.delete.infotext')}`,
            description: `${t('forms.confirmation.bulk_add_tag_confirmation')}`,
            glyph: 'info',
          });
        }
        break;
    }
    setSelectedItems([]);
  };

  return (
    <BulkActionsContainer>
      {/* Uncomment the comments button to enabe bulk addition of workflows and tags */}
      {/* <Button
        type="secondary"
        inline
        aria-describedby="response-action-trigger-workflow"
        onClick={() => setIsTriggerWorkflowDrawerOpen(true)}
        disabled={isBulkActionLoading}
      >
        <Icon
          glyph="triggerWorkflow"
          width="14px"
          height="14px"
          fill="#12344d"
        />
        {t('responses.bulk_actions.trigger_workflows')}
      </Button> */}
      <Workflow
        onSubmit={(workflowId: string) => {
          handleBulkAction(ResponseBulkActionType.TRIGGER_WORKFLOW, [
            workflowId,
          ]);
        }}
        isBulkAction={true}
        disabled={isBulkActionLoading}
      />

      <Button
        type="secondary"
        inline
        aria-describedby="response-action-mark-favourite"
        onClick={() =>
          handleBulkAction(ResponseBulkActionType.MARK_AS_FAVOURITE)
        }
        disabled={isBulkActionLoading}
      >
        <Icon glyph="favourite" width="13px" height="13px" fill="#12344d" />
        {t('workflow.action.action_type.mark_as_favorite')}
      </Button>
      {/* <Button
        type="secondary"
        inline
        aria-describedby="response-action-add-tags"
        onClick={() => setIsAddTagsDrawerOpen(true)}
        disabled={isBulkActionLoading}
      >
        <Icon glyph="tag" width={14} height={14} />
        {t('responses.bulk_actions.add_tags')}
      </Button> */}

      <ResponseTags
        isBulkAction
        handleSubmit={(tagId) =>
          handleBulkAction(ResponseBulkActionType.ADD_TAGS, [tagId])
        }
        disabled={isBulkActionLoading}
      />

      <Button
        type="secondary"
        inline
        aria-describedby="response-action-more-options"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={isBulkActionLoading}
      >
        <Icon glyph="optiondot" width={14} height={14}></Icon>
      </Button>

      <Popover
        id="response-action-more-options"
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <LastSeparateMenu seperator seperatedItemColor="#D91E1E">
          <Menu
            options={options}
            onSelect={(option) => {
              if (option.id == ResponseBulkActionType.MARK_INVALID) {
                setisMarkInvalidDrawerOpen(true);
              } else {
                handleBulkAction(option.id);
              }
            }}
            closeMenuCallback={() => setAnchorEl(null)}
          />
        </LastSeparateMenu>
      </Popover>

      <Confirmation
        width="540px"
        isOpen={isAddTagsDrawerOpen}
        onDismiss={(type) => {
          if (type === 'overlay') return;
          setIsAddTagsDrawerOpen(false);
          setSelectedItems([]);
        }}
        title={`${t('forms.placeholders.add_tags')}`}
        cancelButtonDisabled={isLoading}
        confirmButtonDisabled={!selectedItems.length}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('forms.placeholders.add_tags')}`}
        onConfirm={() => handleBulkAction(ResponseBulkActionType.ADD_TAGS)}
      >
        <SearchTagsAction
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          placeholder={`${t('settings.tags.search_tags_placeholder')}`}
        />
      </Confirmation>

      <Confirmation
        width="540px"
        isOpen={isTriggerWorkflowDrawerOpen}
        onDismiss={(type) => {
          if (type === 'overlay') return;
          setIsTriggerWorkflowDrawerOpen(false);
        }}
        title={`${t('responses.bulk_actions.trigger_workflows')}`}
        cancelButtonDisabled={isLoading}
        confirmButtonDisabled={!selectedItems.length}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('responses.bulk_actions.trigger_workflows')}`}
        onConfirm={() =>
          handleBulkAction(ResponseBulkActionType.TRIGGER_WORKFLOW)
        }
      >
        <TriggerWorkflowAction
          workflowList={workflowList}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          handleFilterChange={handleWorkflowChange}
          isWorkflowsLoading={isWorkflowsLoading}
        />
      </Confirmation>

      <Confirmation
        isOpen={isMarkInvalidDrawerOpen}
        onDismiss={() => {
          setisMarkInvalidDrawerOpen(false);
          setInvalidMessage({ isTouched: false, message: '' });
        }}
        title={`${t('workflow.action.action_type.mark_as_invalid')}`}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('confirm')}`}
        confirmButtonDisabled={invalidMessage?.message?.trim()?.length < 2}
        onConfirm={() => handleBulkAction(ResponseBulkActionType.MARK_INVALID)}
      >
        <StyledInvalidMessage>
          <TextArea
            label={`${t('forms.labels.reason')}`}
            placeholder={`${t('forms.placeholders.type_reason')}`}
            value={invalidMessage?.message}
            onChange={(e: any) =>
              setInvalidMessage({
                isTouched: true,
                message: e.target.value,
              })
            }
            errorText={`${t('forms.err_msg.reason_required')}`}
            isRequired
            isErrored={
              invalidMessage.isTouched &&
              invalidMessage?.message?.trim()?.length === 0
            }
            maxLength={1500}
          />
        </StyledInvalidMessage>
      </Confirmation>
    </BulkActionsContainer>
  );
}

export default ResponsesBulkActions;
